import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Search, Download, Users, MapPin, Phone, Mail, Briefcase, GraduationCap, ChevronLeft, ChevronRight } from 'lucide-react';

const MemberDirectory = () => {
  const { t } = useLanguage();
  const [members, setMembers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [district, setDistrict] = useState('');
  const [districts, setDistricts] = useState([]);
  const [page, setPage] = useState(0);
  const [showExport, setShowExport] = useState(false);
  const LIMIT = 20;

  useEffect(() => {
    fetch('/api/directory/districts')
      .then(r => r.json())
      .then(setDistricts)
      .catch(() => {});
  }, []);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip: page * LIMIT, limit: LIMIT });
      if (search) params.set('search', search);
      if (district) params.set('district', district);
      const res = await fetch(`/api/directory/?${params}`);
      if (res.ok) {
        const data = await res.json();
        setMembers(data.members);
        setTotal(data.total);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search, district, page]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchMembers();
  };

  const exportUrl = (format) => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (district) params.set('district', district);
    return `/api/directory/export/${format}?${params}`;
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="member-directory-page">
      {/* Hero */}
      <section className="bg-party-red py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">
            {t({ en: 'Member Directory', bn: 'সদস্য তালিকা' })}
          </h1>
          <p className="text-lg text-red-100 max-w-2xl mx-auto">
            {t({ en: 'Connect with approved party members across all 64 districts of Bangladesh', bn: 'বাংলাদেশের সকল ৬৪ জেলায় অনুমোদিত দলীয় সদস্যদের সাথে সংযোগ করুন' })}
          </p>
          <p className="text-red-200 mt-2 text-sm">{total} {t({ en: 'approved members', bn: 'অনুমোদিত সদস্য' })}</p>
        </div>
      </section>

      {/* Search + Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        <Card className="shadow-lg" data-testid="directory-search-card">
          <CardContent className="p-4">
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder={t({ en: 'Search by name, phone or profession...', bn: 'নাম, ফোন বা পেশা দিয়ে খুঁজুন...' })}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="directory-search-input"
                />
              </div>
              <select
                value={district}
                onChange={e => { setDistrict(e.target.value); setPage(0); }}
                className="border rounded-md px-3 py-2 text-sm bg-white min-w-[180px]"
                data-testid="directory-district-filter"
              >
                <option value="">{t({ en: 'All Districts', bn: 'সকল জেলা' })}</option>
                {districts.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <Button type="submit" className="bg-party-red hover:bg-party-red-600" data-testid="directory-search-btn">
                <Search className="h-4 w-4 mr-2" />{t({ en: 'Search', bn: 'খুঁজুন' })}
              </Button>
              <div className="relative">
                <Button type="button" variant="outline" onClick={() => setShowExport(!showExport)} data-testid="export-btn">
                  <Download className="h-4 w-4 mr-2" />{t({ en: 'Export', bn: 'ডাউনলোড' })}
                </Button>
                {showExport && (
                  <div className="absolute right-0 top-full mt-1 bg-white border rounded-md shadow-lg z-10 min-w-[140px]">
                    <a href={exportUrl('csv')} download className="block px-4 py-2 text-sm hover:bg-gray-50" data-testid="export-csv">CSV</a>
                    <a href={exportUrl('excel')} download className="block px-4 py-2 text-sm hover:bg-gray-50" data-testid="export-excel">Excel (.xlsx)</a>
                    <a href={exportUrl('pdf')} download className="block px-4 py-2 text-sm hover:bg-gray-50" data-testid="export-pdf">PDF</a>
                  </div>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Members Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-party-red"></div>
          </div>
        ) : members.length === 0 ? (
          <div className="text-center py-16">
            <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">{t({ en: 'No approved members found', bn: 'কোনো অনুমোদিত সদস্য পাওয়া যায়নি' })}</p>
            <p className="text-gray-400 text-sm mt-1">{t({ en: 'Try adjusting your search or filter', bn: 'আপনার অনুসন্ধান বা ফিল্টার পরিবর্তন করুন' })}</p>
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {members.map(m => (
                <Card key={m._id} className="hover:shadow-lg transition-shadow border-l-4 border-l-party-green" data-testid={`member-card-${m._id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-full bg-party-green-50 flex items-center justify-center flex-shrink-0">
                        <Users className="h-6 w-6 text-party-green" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-gray-900 truncate">{m.full_name}</h3>
                        {m.district && (
                          <p className="text-sm text-party-red flex items-center gap-1 mt-0.5">
                            <MapPin className="h-3 w-3" />{m.district}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="mt-3 space-y-1.5 text-sm text-gray-600">
                      {m.profession && (
                        <p className="flex items-center gap-2"><Briefcase className="h-3.5 w-3.5 text-gray-400" />{m.profession}</p>
                      )}
                      {m.education && (
                        <p className="flex items-center gap-2"><GraduationCap className="h-3.5 w-3.5 text-gray-400" />{m.education}</p>
                      )}
                      {m.mobile && (
                        <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-gray-400" />{m.mobile}</p>
                      )}
                      {m.email && (
                        <p className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-gray-400" />{m.email}</p>
                      )}
                      {m.present_address && (
                        <p className="flex items-center gap-2 text-xs text-gray-400"><MapPin className="h-3 w-3" />{m.present_address}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-8" data-testid="directory-pagination">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-gray-600">
                  {t({ en: `Page ${page + 1} of ${totalPages}`, bn: `পৃষ্ঠা ${page + 1} / ${totalPages}` })}
                </span>
                <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MemberDirectory;
